import React, { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { exportBrandedPdf } from '../utils/brandedPdf';
import '../Pages.css';
import Pagination from '../components/Pagination';
import { formatDate } from '../utils/dateFormat';

// Batch screening allowance: once-off (batch_screening_limit) and monthly
// (batch_screening_limit_monthly). Both blank = unlimited; when one is set,
// a blank one means none.
const batchLimitText = (pkg, key) => {
  if (pkg[key] != null) return pkg[key];
  return pkg.batch_screening_limit == null && pkg.batch_screening_limit_monthly == null ? 'Unlimited' : 'None';
};
const BATCH_HINT = 'Each month uses its monthly allowance first, then the once-off allowance. Leave both blank for unlimited; if one is set, a blank one means none.';
const PRICE_HINT = 'A new monthly price is invoiced from the 1st of next month; earlier months keep the price they had.';

export default function ManagePackages() {
  const [packages, setPackages] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [msg, setMsg] = useState('');
  const [showMsg, setShowMsg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Create package state
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPkg, setNewPkg] = useState({
    name: '',
    user_limit: '',
    onboarding_screening_limit: '',
    batch_screening_limit: '',
    batch_screening_limit_monthly: '',
    price_monthly: '',
    price_annual: ''
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/packages');
      setPackages(res.data);
      setLoading(false);
    } catch (e) {
      setPackages([]);
      setError(e.response?.data?.error || e.message || 'Failed to fetch packages');
      setLoading(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditId(pkg.id);
    setEditData({ ...pkg });
  };

  const handleCancel = () => {
    setEditId(null);
    setEditData({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    // Inputs hold text (and prices come back from the API as text): send numbers.
    const payload = {
      name: String(editData.name || '').trim(),
      user_limit: normalizeNumber(editData.user_limit),
      onboarding_screening_limit: normalizeNumber(editData.onboarding_screening_limit),
      batch_screening_limit: normalizeNumber(editData.batch_screening_limit),
      batch_screening_limit_monthly: normalizeNumber(editData.batch_screening_limit_monthly),
      price_monthly: normalizeNumber(editData.price_monthly),
      price_annual: normalizeNumber(editData.price_annual),
      allow_export: !!editData.allow_export,
      allow_audit_trail: !!editData.allow_audit_trail,
      allow_batch_screening: !!editData.allow_batch_screening,
      allow_system_integration: !!editData.allow_system_integration,
      notes: editData.notes ?? null
    };
    if (!payload.name) {
      setMsg('❌ Package name is required');
      setShowMsg(true);
      return;
    }
    try {
      const res = await axios.put(`/packages/${editId}`, payload);
      const from = res.data?.package?.price_effective_from;
      setMsg(from ? `✅ Package updated. The new monthly price is invoiced from ${formatDate(from)}; earlier months keep the old price.` : '✅ Package updated');
      setShowMsg(true);
      setEditId(null);
      setEditData({});
      fetchPackages();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.error || e.message));
      setShowMsg(true);
    }
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setNewPkg(prev => ({ ...prev, [name]: value }));
  };

  const normalizeNumber = (val) => {
    if (val === '' || val === null || val === undefined) return null;
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newPkg.name.trim()) {
      setMsg('❌ Package name is required');
      setShowMsg(true);
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: newPkg.name.trim(),
        user_limit: normalizeNumber(newPkg.user_limit),
        onboarding_screening_limit: normalizeNumber(newPkg.onboarding_screening_limit),
        batch_screening_limit: normalizeNumber(newPkg.batch_screening_limit),
        batch_screening_limit_monthly: normalizeNumber(newPkg.batch_screening_limit_monthly),
        price_monthly: normalizeNumber(newPkg.price_monthly),
        price_annual: normalizeNumber(newPkg.price_annual),
        // Sensible defaults for options not requested explicitly
        allow_export: true,
        allow_audit_trail: true,
        allow_batch_screening: normalizeNumber(newPkg.batch_screening_limit) !== null ||
          normalizeNumber(newPkg.batch_screening_limit_monthly) !== null,
        allow_system_integration: false,
        notes: null
      };
      await axios.post('/packages', payload);
      setMsg('✅ Package created');
      setShowMsg(true);
      setNewPkg({ name: '', user_limit: '', onboarding_screening_limit: '', batch_screening_limit: '', batch_screening_limit_monthly: '', price_monthly: '', price_annual: '' });
      setShowCreateForm(false);
      fetchPackages();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.error || e.message));
      setShowMsg(true);
    } finally {
      setCreating(false);
    }
  };
  
  // Filter packages based on search term
  const filtered = searchTerm
    ? packages.filter(pkg =>
        Object.values(pkg).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : packages;
    
  // Calculate pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentPackages = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
  // Prepare data for export
  const exportData = filtered.map(pkg => ({
    'Name': pkg.name,
    'User Limit': pkg.user_limit ?? 'Unlimited',
    'Onboarding Limit': pkg.onboarding_screening_limit ?? 'Unlimited',
    'Once-off Batch Limit': batchLimitText(pkg, 'batch_screening_limit'),
    'Monthly Batch Limit': batchLimitText(pkg, 'batch_screening_limit_monthly'),
    'Monthly Price': pkg.price_monthly,
    'Annual Price': pkg.price_annual
  }));
  
  // Export functions
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Packages');
    XLSX.writeFile(wb, 'packages.xlsx');
  };
  
  const exportPDF = () => exportBrandedPdf({
    title: 'PACKAGES — REPORT',
    searchValue: searchTerm ? `"${searchTerm}"` : 'All packages',
    fileName: 'Packages_Report',
    columns: [
      { header: 'Name', width: 18, bold: true }, { header: 'User Limit', width: 12, align: 'center' },
      { header: 'Onboarding Limit', width: 14, align: 'center' }, { header: 'Once-off Batch Limit', width: 12, align: 'center' },
      { header: 'Monthly Batch Limit', width: 12, align: 'center' },
      { header: 'Monthly Price', width: 12, align: 'right' }, { header: 'Annual Price', width: 12, align: 'right' }
    ],
    rows: exportData.map(o => Object.values(o))
  });

  return (
    <div className="page-container">
      {showMsg && (
        <div className="message-popup">
          <div className="message-popup-content">
            <p>{msg}</p>
            <button className="close-popup-button" onClick={() => setShowMsg(false)}>Close</button>
          </div>
        </div>
      )}
      
      <div className="table-controls">
        <input
          type="text"
          placeholder="Search packages..."
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <div className="button-group">
          <button
            className="export-button"
            onClick={() => setShowCreateForm(v => !v)}
            type="button"
          >
            {showCreateForm ? 'Hide Create Package' : 'Create Package'}
          </button>
          <CSVLink filename="packages.csv" data={exportData} className="export-button">Export CSV</CSVLink>
          <button className="export-button" onClick={exportExcel}>Export Excel</button>
          <button className="export-button" onClick={exportPDF}>Export PDF</button>
        </div>
      </div>

      {/* Create Package */}
      {showCreateForm && (
      <div className="form-section" style={{ marginTop: '1rem' }}>
        <h3 className="form-section-title" style={{ marginBottom: '0.75rem' }}>Create Package</h3>
        <form onSubmit={handleCreate}>
          <div className="form-row">
            <div className="step-form-group">
              <label className="required">Package Name</label>
              <input
                name="name"
                placeholder="Enter name (e.g., Custom, Gold)"
                value={newPkg.name}
                onChange={handleCreateChange}
                required
              />
            </div>
            <div className="step-form-group">
              <label>User Limit</label>
              <input
                type="number"
                name="user_limit"
                placeholder="Leave blank for Unlimited"
                value={newPkg.user_limit}
                onChange={handleCreateChange}
                min="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="step-form-group">
              <label>Onboarding Limit</label>
              <input
                type="number"
                name="onboarding_screening_limit"
                placeholder="Leave blank for Unlimited"
                value={newPkg.onboarding_screening_limit}
                onChange={handleCreateChange}
                min="0"
              />
            </div>
            <div className="step-form-group">
              <label>Once-off Batch Limit</label>
              <input
                type="number"
                name="batch_screening_limit"
                placeholder="Batch screenings granted once"
                value={newPkg.batch_screening_limit}
                onChange={handleCreateChange}
                min="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="step-form-group">
              <label>Monthly Batch Limit</label>
              <input
                type="number"
                name="batch_screening_limit_monthly"
                placeholder="Batch screenings every month"
                value={newPkg.batch_screening_limit_monthly}
                onChange={handleCreateChange}
                min="0"
              />
            </div>
            <div className="step-form-group">
              <p style={{ margin: '1.6rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{BATCH_HINT}</p>
            </div>
          </div>

          <div className="form-row">
            <div className="step-form-group">
              <label>Monthly Price (N$)</label>
              <input
                type="number"
                step="0.01"
                name="price_monthly"
                placeholder="0.00"
                value={newPkg.price_monthly}
                onChange={handleCreateChange}
                min="0"
              />
            </div>
            <div className="step-form-group">
              <label>Annual Price (N$)</label>
              <input
                type="number"
                step="0.01"
                name="price_annual"
                placeholder="0.00"
                value={newPkg.price_annual}
                onChange={handleCreateChange}
                min="0"
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="export-button" disabled={creating}>
              {creating ? 'Creating...' : 'Create Package'}
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setNewPkg({ name: '', user_limit: '', onboarding_screening_limit: '', batch_screening_limit: '', batch_screening_limit_monthly: '', price_monthly: '', price_annual: '' })}
              disabled={creating}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setShowCreateForm(false)}
              disabled={creating}
            >
              Close
            </button>
          </div>
        </form>
      </div>
      )}
      
      <div style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1rem' }}>
        Showing {currentPackages.length} of {filtered.length} packages {searchTerm ? `(filtered from ${packages.length})` : ''}
      </div>
      
      <div className="table-container">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading packages...</div>
        ) : error ? (
          <div style={{ color: 'red', textAlign: 'center', padding: '2rem' }}>{error}</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>No packages found.</div>
        ) : (
        <table className="pips-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>User Limit</th>
              <th>Onboarding Limit</th>
              <th title={BATCH_HINT}>Once-off Batch Limit</th>
              <th title={BATCH_HINT}>Monthly Batch Limit</th>
              <th title={PRICE_HINT}>Monthly Price</th>
              <th>Annual Price</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentPackages.map((pkg, idx) => (
              <tr key={pkg.id}>
                <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                <td>{pkg.name}</td>
                <td>
                  {editId === pkg.id ? (
                    <input name="user_limit" type="number" value={editData.user_limit ?? ''} onChange={handleChange} />
                  ) : (
                    pkg.user_limit ?? 'Unlimited'
                  )}
                </td>
                <td>
                  {editId === pkg.id ? (
                    <input name="onboarding_screening_limit" type="number" value={editData.onboarding_screening_limit ?? ''} onChange={handleChange} />
                  ) : (
                    pkg.onboarding_screening_limit ?? 'Unlimited'
                  )}
                </td>
                <td>
                  {editId === pkg.id ? (
                    <input name="batch_screening_limit" type="number" min="0" value={editData.batch_screening_limit ?? ''} onChange={handleChange} />
                  ) : (
                    batchLimitText(pkg, 'batch_screening_limit')
                  )}
                </td>
                <td>
                  {editId === pkg.id ? (
                    <input name="batch_screening_limit_monthly" type="number" min="0" value={editData.batch_screening_limit_monthly ?? ''} onChange={handleChange} />
                  ) : (
                    batchLimitText(pkg, 'batch_screening_limit_monthly')
                  )}
                </td>
                <td>
                  {editId === pkg.id ? (
                    <input name="price_monthly" type="number" value={editData.price_monthly ?? ''} onChange={handleChange} />
                  ) : (
                    pkg.price_monthly
                  )}
                </td>
                <td>
                  {editId === pkg.id ? (
                    <input name="price_annual" type="number" value={editData.price_annual ?? ''} onChange={handleChange} />
                  ) : (
                    pkg.price_annual
                  )}
                </td>
                <td>
                  {editId === pkg.id ? (
                    <>
                      <button className="export-button" onClick={handleSave} type="button">Save</button>
                      <button className="btn-cancel" onClick={handleCancel} type="button">Cancel</button>
                    </>
                  ) : (
                    <button className="export-button" onClick={() => handleEdit(pkg)} type="button">Edit</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
      
      <Pagination
        currentPage={currentPage}
        totalItems={filtered.length}
        itemsPerPage={itemsPerPage}
        itemLabel="package"
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
      />
    </div>
  );
}
